import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MuiDialog from "@mui/material/Dialog";
import MuiIconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { api } from "../utils/helpers";
import IflowDiagram from "./IflowDiagram";

// ── Utils-Udo: BPMN Parsing & Diff ──────────────────────────────────────

function parseBpmnElements(xmlStr){
  try{
    const p=new DOMParser();const doc=p.parseFromString(xmlStr,"application/xml");
    const res={};
    const tags=["serviceTask","scriptTask","callActivity","CallActivity","task","receiveTask","sendTask",
      "exclusiveGateway","inclusiveGateway","parallelGateway","eventBasedGateway",
      "startEvent","endEvent","intermediateCatchEvent","intermediateThrowEvent","subProcess","participant","messageFlow"];
    for(const tag of tags){
      const els=doc.getElementsByTagNameNS("*",tag);
      for(const el of els){
        const id=el.getAttribute("id");
        if(!id)continue;
        const name=el.getAttribute("name")||"";
        const properties={};
        const propEls=el.getElementsByTagNameNS("*","property");
        for(const pe of propEls){
          const keyEl=pe.getElementsByTagNameNS("*","key")[0];
          const valEl=pe.getElementsByTagNameNS("*","value")[0];
          const k=keyEl?keyEl.textContent.trim():pe.getAttribute("key")||"";
          const v2=valEl?valEl.textContent.trim():pe.getAttribute("value")||"";
          if(k)properties[k]=v2;
        }
        const bpmnType=el.localName||tag;
        const sortedEntries=Object.entries(properties).sort(([a],[b])=>a.localeCompare(b));
        const fingerprint=name+"|"+sortedEntries.map(([k,v2])=>k+"="+v2).join("|");
        const activityType=properties.activityType||"";
        const subActivityType=properties.subActivityType||"";
        const cmdUri=properties.cmdVariantUri||"";
        const cnameMatch=cmdUri.match(/cname::([^/]+)/);
        const cname=cnameMatch?cnameMatch[1]:"";
        res[id]={name,fingerprint,type:bpmnType,properties,activityType,subActivityType,cname};
      }
    }
    return res;
  }catch{return{};}
}

function diffBpmnElements(before,after){
  const added=[],removed=[],changed=[],unchanged=[];
  const allIds=new Set([...Object.keys(before),...Object.keys(after)]);
  for(const id of allIds){
    if(!before[id])added.push(id);
    else if(!after[id])removed.push(id);
    else if(before[id].fingerprint!==after[id].fingerprint)changed.push(id);
    else unchanged.push(id);
  }
  return{added,removed,changed,unchanged};
}

// ── Script-Line-Diff (GitHub-Stil, LCS-basiert) ──────────────────────────
// before = Ziel-/Vorher-Inhalt, after = Quell-/Nachher-Inhalt.
// Rueckgabe: [{type:'ctx'|'add'|'del', text, aLine, bLine}]
function computeLineDiff(before, after){
  const A=(before==null?"":String(before)).split("\n");
  const B=(after==null?"":String(after)).split("\n");
  const n=A.length, m=B.length;
  const MAX=2500; // Schutz vor O(n*m)-Blowup bei Riesen-Skripten
  if(n>MAX||m>MAX){
    return [...A.map((t,i)=>({type:"del",text:t,aLine:i+1})),...B.map((t,i)=>({type:"add",text:t,bLine:i+1}))];
  }
  const dp=Array.from({length:n+1},()=>new Uint16Array(m+1));
  for(let i=n-1;i>=0;i--)for(let j=m-1;j>=0;j--){
    dp[i][j]=A[i]===B[j]?dp[i+1][j+1]+1:Math.max(dp[i+1][j],dp[i][j+1]);
  }
  const out=[]; let i=0,j=0;
  while(i<n&&j<m){
    if(A[i]===B[j]){out.push({type:"ctx",text:A[i],aLine:i+1,bLine:j+1});i++;j++;}
    else if(dp[i+1][j]>=dp[i][j+1]){out.push({type:"del",text:A[i],aLine:i+1});i++;}
    else {out.push({type:"add",text:B[j],bLine:j+1});j++;}
  }
  while(i<n){out.push({type:"del",text:A[i],aLine:i+1});i++;}
  while(j<m){out.push({type:"add",text:B[j],bLine:j+1});j++;}
  return out;
}

// Zuordnung vom internen Zustandswert auf den Katalogschluessel. Der Wert selbst
// bleibt, weil unten damit verglichen wird; uebersetzt wird nur die Beschriftung.
const SKRIPT_STATUS_KEY={"neu":"neu","entfernt":"entfernt","geändert":"geaendert","unverändert":"unveraendert"};

// Eine Datei im Diff-Block (aufklappbar). before/after = target/source-Inhalt.
function ScriptDiffFile({name, before, after}){
  const {t}=useTranslation("gemeinsam");
  const rows=useMemo(()=>computeLineDiff(before, after),[before,after]);
  const adds=rows.filter(r=>r.type==="add").length;
  const dels=rows.filter(r=>r.type==="del").length;
  const status=before==null?"neu":after==null?"entfernt":(adds||dels)?"geändert":"unverändert";
  const [open,setOpen]=useState(status!=="unveraendert");
  const statusColor=status==="neu"?"var(--iio-success)":status==="entfernt"?"var(--iio-critical)":status==="geaendert"?"var(--iio-warning)":"var(--iio-text-3)";
  return <div style={{border:"1px solid var(--iio-border)",borderRadius:"var(--iio-r-lg)",overflow:"hidden",marginBottom:10,background:"var(--iio-bg-1)"}}>
    <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",cursor:"pointer",background:"var(--iio-bg-2)",borderBottom:open?"1px solid var(--iio-border)":"none"}}>
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="var(--iio-text-3)" strokeWidth="2" style={{transform:open?"rotate(90deg)":"none",transition:"transform .12s",flexShrink:0}}><path d="M6 4l4 4-4 4"/></svg>
      <span style={{fontFamily:"var(--iio-font-mono)",fontSize:12,color:"var(--iio-text-1)",fontWeight:600,flex:1,wordBreak:"break-all"}}>{name}</span>
      <span style={{fontFamily:"var(--iio-font-mono)",fontSize:10,fontWeight:700,letterSpacing:".06em",color:statusColor,textTransform:"uppercase"}}>{t(`bauteil.vergleich.skriptStatus.${SKRIPT_STATUS_KEY[status]}`)}</span>
      {(adds>0||dels>0)&&<span style={{fontFamily:"var(--iio-font-mono)",fontSize:11,display:"flex",gap:6}}>
        <span style={{color:"var(--iio-success)"}}>+{adds}</span><span style={{color:"var(--iio-critical)"}}>-{dels}</span>
      </span>}
    </div>
    {open&&<div style={{overflow:"auto",maxHeight:"52vh"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:"var(--iio-font-mono)",fontSize:12,lineHeight:1.55}}>
        <tbody>
          {rows.map((r,idx)=>{
            const bg=r.type==="add"?"var(--iio-diff-add-bg)":r.type==="del"?"var(--iio-diff-del-bg)":"transparent";
            const sign=r.type==="add"?"+":r.type==="del"?"-":" ";
            const signCol=r.type==="add"?"var(--iio-success)":r.type==="del"?"var(--iio-critical)":"var(--iio-text-3)";
            return <tr key={idx} style={{background:bg}}>
              <td style={{width:44,textAlign:"right",padding:"0 6px",color:"var(--iio-text-4)",userSelect:"none",borderRight:"1px solid var(--iio-border-subtle)"}}>{r.aLine||""}</td>
              <td style={{width:44,textAlign:"right",padding:"0 6px",color:"var(--iio-text-4)",userSelect:"none",borderRight:"1px solid var(--iio-border-subtle)"}}>{r.bLine||""}</td>
              <td style={{width:16,textAlign:"center",color:signCol,userSelect:"none",fontWeight:700}}>{sign}</td>
              <td style={{padding:"0 8px",color:"var(--iio-text-1)",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{r.text||" "}</td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>}
  </div>;
}

// Script-Diff-Ansicht: alle Skripte des iFlows, geaenderte zuerst.
// Backend liefert pro Datei {before: Ziel-/Vorher-Inhalt, after: Quell-/Nachher-Inhalt}.
function ScriptDiffView({scripts}){
  const {t}=useTranslation("gemeinsam");
  const names=Object.keys(scripts||{}).sort((a,b)=>{
    const ca=scripts[a]?.before!==scripts[a]?.after, cb=scripts[b]?.before!==scripts[b]?.after;
    if(ca!==cb)return ca?-1:1; return a.localeCompare(b);
  });
  if(!names.length) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"var(--iio-text-3)"}}>{t("bauteil.vergleich.keineSkripte")}</div>;
  const changed=names.filter(n=>scripts[n]?.before!==scripts[n]?.after).length;
  return <div style={{flex:1,overflow:"auto",minHeight:0}}>
    <div style={{fontSize:12,color:"var(--iio-text-2)",marginBottom:10}}>
      {changed>0
        ?t("bauteil.vergleich.skripteGeaendert",{anzahl:changed,count:names.length})
        :t("bauteil.vergleich.skripteUnveraendert",{count:names.length})}
    </div>
    {names.map(n=><ScriptDiffFile key={n} name={n} before={scripts[n]?.before} after={scripts[n]?.after}/>)}
  </div>;
}

function parseContentModifierXml(val){
  if(!val||typeof val!=="string")return null;
  if(!val.includes("<row>")&&!val.includes("<row "))return null;
  try{
    const rows=[];
    const rowRe=/<row[^>]*>([\s\S]*?)<\/row>/g;
    let rm;
    while((rm=rowRe.exec(val))!==null){
      const cellRe=/<cell\s+id=["']([^"']*)["'][^>]*>([\s\S]*?)<\/cell>|<cell\s+id=["']([^"']*)["'][^>]*\/>/g;
      const row={};let cm;
      while((cm=cellRe.exec(rm[1]))!==null){
        const key=cm[1]||cm[3];const v=cm[2]!==undefined?cm[2]:"";
        if(key)row[key]=v;
      }
      if(Object.keys(row).length)rows.push(row);
    }
    if(!rows.length)return null;
    const ORDER=["Action","Type","Name","Value","Default","Datatype"];
    const cols=[...new Set([...ORDER,...rows.flatMap(r=>Object.keys(r))])].filter(k=>rows.some(r=>k in r));
    return{rows,cols};
  }catch{return null;}
}

// Zeilen-Diff zweier Content-Modifier-Tabellen (headerTable/propertyTable u. ae.).
// Vorher wurden BEIDE Tabellen in voller Laenge nebeneinander gerendert - bei einer
// einzigen geaenderten Zeile sahen sie praktisch identisch aus und der Anwender fand
// den Unterschied nicht. Schluessel je Zeile ist die Name-Zelle: sie bleibt bei
// Wertaenderungen stabil und macht "geaendert" von "neu + entfernt" unterscheidbar.
function diffContentModifierRows(beforeVal, afterVal){
  const b=parseContentModifierXml(beforeVal), a=parseContentModifierXml(afterVal);
  if(!b&&!a)return null;
  const cols=[...new Set([...(b?.cols||[]),...(a?.cols||[])])];
  const keyed=(rows)=>{ const used=new Set(), m=new Map(); (rows||[]).forEach((r,i)=>{
    const base=r.Name!==undefined&&r.Name!==""?("n:"+r.Name):("i:"+i);
    let k=base,c=1; while(used.has(k))k=base+"#"+(c++); used.add(k); m.set(k,r);
  }); return m; };
  const bMap=keyed(b?.rows), aMap=keyed(a?.rows);
  const rows=[]; const seen=new Set();
  for(const [k,ar] of aMap){
    seen.add(k);
    const br=bMap.get(k);
    if(!br){rows.push({st:"add",cells:ar});continue;}
    const changedCols=cols.filter(c=>(br[c]||"")!==(ar[c]||""));
    rows.push(changedCols.length?{st:"chg",cells:ar,before:br,changedCols}:{st:"same",cells:ar});
  }
  for(const [k,br] of bMap){ if(!seen.has(k)) rows.push({st:"del",cells:br}); }
  return {cols,rows,hasDiff:rows.some(r=>r.st!=="same")};
}

// ── Inspector-Ina: Element Inspect Popup ─────────────────────────────────

function ElementInspectPopup({element,side,parsedBefore,parsedAfter,diffIds,onClose,scripts}){
  const{t}=useTranslation("gemeinsam");
  if(!element)return null;
  const elId=element.id||element;
  const info=side==="before"?parsedBefore[elId]:parsedAfter[elId];
  if(!info)return null;
  // Status auf BEIDEN Seiten: im Vorher-Panel stand bisher immer "Unveraendert",
  // auch wenn das Element geaendert oder entfernt war - links und rechts
  // widersprachen sich fuer dasselbe Element.
  let statusKey="unveraendert";
  if(diffIds){
    if(side==="before"){
      if(diffIds.removed.includes(elId))statusKey="entfernt";
      else if(diffIds.changed.includes(elId))statusKey="geaendert";
    }else{
      if(diffIds.added.includes(elId))statusKey="neu";
      else if(diffIds.removed.includes(elId))statusKey="entfernt";
      else if(diffIds.changed.includes(elId))statusKey="geaendert";
    }
  }
  const STATUS_COL={neu:"var(--iio-dia-diff-add)",entfernt:"var(--iio-dia-diff-del)",geaendert:"var(--iio-dia-diff-chg)",unveraendert:"var(--iio-text-3)"};
  const status=t("bauteil.vergleich.elementStatus."+statusKey);
  const statusColor=STATUS_COL[statusKey];
  // BPMN-Typnamen bleiben stehen: sie heissen in der Integration Suite in beiden
  // Sprachen so, uebersetzt findet der Nutzer sie in SAP nicht wieder.
  const typeLabels={serviceTask:"Service Task",scriptTask:"Script Task",callActivity:"Call Activity",
    exclusiveGateway:"Router (Exclusive)",inclusiveGateway:"Inclusive Gateway",parallelGateway:"Parallel Gateway",
    startEvent:"Start Event",endEvent:"End Event",subProcess:"Sub Process",participant:"Participant",
    task:"Task",receiveTask:"Receive Task",sendTask:"Send Task",
    intermediateCatchEvent:"Intermediate Catch Event",intermediateThrowEvent:"Intermediate Throw Event"};
  const typeName=typeLabels[info.type]||info.type;
  let propDiffRows=null;
  if(diffIds&&diffIds.changed.includes(elId)&&parsedBefore[elId]&&parsedAfter[elId]){
    const bProps=parsedBefore[elId].properties||{};
    const aProps=parsedAfter[elId].properties||{};
    const allKeys=new Set([...Object.keys(bProps),...Object.keys(aProps)]);
    propDiffRows=[];
    for(const k of allKeys){
      const bv=bProps[k]!==undefined?bProps[k]:null;
      const av=aProps[k]!==undefined?aProps[k]:null;
      if(bv!==av)propDiffRows.push({key:k,before:bv,after:av});
    }
  }
  // iio-Karte statt des alten Dunkelblau/Teal-Looks: die Farben kommen komplett
  // aus den Tokens, damit das Popup in beiden Themes zur restlichen Seite gehoert.
  return <div style={{
    position:"absolute",top:8,right:8,left:typeof window!=="undefined"&&window.innerWidth<768?8:undefined,width:typeof window!=="undefined"&&window.innerWidth<768?"auto":352,maxHeight:440,overflow:"auto",
    background:"var(--iio-bg-2)",
    border:"1px solid var(--iio-border)",
    borderTop:`2px solid ${statusColor}`,
    borderRadius:"var(--iio-r-lg)",
    boxShadow:"0 12px 32px rgba(0,0,0,.35)",
    zIndex:20,fontSize:11
  }}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"9px 12px",
      borderBottom:"1px solid var(--iio-border)",background:"var(--iio-bg-3)"}}>
      <span style={{fontWeight:600,color:"var(--iio-text-1)",fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{info.name||elId}</span>
      <button onClick={onClose} aria-label={t("aktion.schliessen","Schließen")} style={{background:"none",border:"none",cursor:"pointer",color:"var(--iio-text-3)",fontSize:16,lineHeight:1,padding:0,flexShrink:0}}>&times;</button>
    </div>
    <div style={{padding:"8px 12px"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <span style={{fontSize:10,color:"var(--iio-text-2)"}}>{typeName}</span>
        {(info.activityType||info.subActivityType||info.cname)&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:4,
          background:"var(--iio-bg-1)",border:"1px solid var(--iio-border-subtle)",
          color:"var(--iio-text-2)",fontFamily:"var(--iio-font-mono)"}}>{info.activityType||(info.subActivityType||info.cname)}</span>}
        <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,border:`1px solid ${statusColor}`,color:statusColor,fontWeight:700}}>{status}</span>
      </div>
      <div style={{fontSize:9,color:"var(--iio-text-3)",fontFamily:"var(--iio-font-mono)",marginBottom:8}}>ID: {elId}</div>
      {propDiffRows&&propDiffRows.length>0&&<div>
        <div style={{fontSize:9,fontWeight:600,color:"var(--iio-text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("bauteil.vergleich.geaenderteProperties")}</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"var(--iio-font-mono)"}}>
          <thead><tr style={{borderBottom:"1px solid var(--iio-border-subtle)"}}>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--iio-text-3)",fontWeight:600,fontSize:9}}>{t("bauteil.vergleich.spalteProperty")}</th>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--iio-text-3)",fontWeight:600,fontSize:9}}>{t("bauteil.vergleich.spalteVorher")}</th>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--iio-text-3)",fontWeight:600,fontSize:9}}>{t("bauteil.vergleich.spalteNachher")}</th>
          </tr></thead>
          <tbody>{propDiffRows.map(r=>{
            // Tabellen-Properties (Content Modifier): EIN Zeilen-Diff statt zweier
            // fast identischer Volltabellen - nur so ist die geaenderte Zeile findbar.
            const cmDiff=diffContentModifierRows(r.before,r.after);
            if(cmDiff){
              const ROW_BG={add:"var(--iio-diff-add-bg)",del:"var(--iio-diff-del-bg)",chg:"var(--iio-diff-chg-bg)",same:"transparent"};
              const SIGN={add:["+","var(--iio-success)",t("bauteil.vergleich.zeileNeu")],del:["\u2212","var(--iio-critical)",t("bauteil.vergleich.zeileWeg")],chg:["\u0394","var(--iio-warning)",t("bauteil.vergleich.zeileGeaendert")],same:["","",""]};
              return <tr key={r.key}>
                <td style={{padding:"3px 4px",color:"var(--iio-text-1)",fontWeight:500,verticalAlign:"top",maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.key}</td>
                <td colSpan={2} style={{padding:"3px 0"}}>
                  <div style={{fontSize:9,overflow:"auto",maxHeight:170,border:"1px solid var(--iio-border-subtle)",borderRadius:4}}>
                    <table style={{borderCollapse:"collapse",width:"100%"}}>
                      <thead><tr>
                        <th style={{width:14}} />
                        {cmDiff.cols.map(c=><th key={c} style={{padding:"2px 4px",color:"var(--iio-text-3)",fontWeight:600,textAlign:"left",borderBottom:"1px solid var(--iio-border-subtle)",whiteSpace:"nowrap"}}>{c}</th>)}
                      </tr></thead>
                      <tbody>{cmDiff.rows.map((row,i)=>{
                        const [sign,signCol,rowTitle]=SIGN[row.st];
                        return <tr key={i} style={{background:ROW_BG[row.st]}} title={rowTitle||undefined}>
                          <td style={{textAlign:"center",color:signCol,fontWeight:700,userSelect:"none"}}>{sign}</td>
                          {cmDiff.cols.map(c=>{
                            const changed=row.st==="chg"&&row.changedCols.includes(c);
                            if(changed){
                              return <td key={c} style={{padding:"2px 4px",borderBottom:"1px solid var(--iio-border-subtle)",whiteSpace:"nowrap"}}>
                                <span style={{color:"var(--iio-critical)",textDecoration:"line-through"}}>{row.before[c]||"\u2014"}</span>
                                <span style={{color:"var(--iio-text-3)"}}>{" \u2192 "}</span>
                                <span style={{color:"var(--iio-success)",fontWeight:600}}>{row.cells[c]||"\u2014"}</span>
                              </td>;
                            }
                            return <td key={c} style={{padding:"2px 4px",color:row.st==="del"?"var(--iio-text-3)":"var(--iio-text-1)",textDecoration:row.st==="del"?"line-through":"none",borderBottom:"1px solid var(--iio-border-subtle)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.cells[c]||"\u2014"}</td>;
                          })}
                        </tr>;
                      })}</tbody>
                    </table>
                  </div>
                </td>
              </tr>;
            }
            const renderPropVal=(val,color)=><span style={{color}}>{val===null?"\u2014":val||t("bauteil.vergleich.leer")}</span>;
            return <tr key={r.key} style={{background:"var(--iio-diff-chg-bg)"}}>
              <td style={{padding:"3px 4px",color:"var(--iio-text-1)",fontWeight:500,maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.key}</td>
              <td style={{padding:"3px 4px",wordBreak:"break-all"}}>{renderPropVal(r.before,r.before===null?"var(--iio-text-3)":"var(--iio-critical)")}</td>
              <td style={{padding:"3px 4px",wordBreak:"break-all"}}>{renderPropVal(r.after,r.after===null?"var(--iio-text-3)":"var(--iio-success)")}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>}
      {propDiffRows&&propDiffRows.length===0&&<div style={{fontSize:10,color:"var(--iio-text-3)"}}>{t("bauteil.vergleich.keinePropertyUnterschiede")}</div>}
      {!propDiffRows&&info.properties&&Object.keys(info.properties).length>0&&<div>
        <div style={{fontSize:9,fontWeight:600,color:"var(--iio-text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("bauteil.vergleich.properties")}</div>
        <div style={{maxHeight:150,overflow:"auto"}}>
          {Object.entries(info.properties).map(([k,val])=>{
            const parsed=parseContentModifierXml(val);
            return <div key={k} style={{display:"flex",gap:4,fontSize:10,padding:"2px 0",borderBottom:"1px solid var(--iio-border-subtle)",flexDirection:parsed?"column":"row"}}>
              <span style={{color:"var(--iio-text-2)",fontFamily:"var(--iio-font-mono)",minWidth:80}}>{k}</span>
              {parsed?<div style={{fontSize:9,overflow:"auto",maxHeight:120}}>
                <table style={{borderCollapse:"collapse",width:"100%"}}>
                  <thead><tr>{parsed.cols.map(c=><th key={c} style={{padding:"2px 4px",color:"var(--iio-text-3)",fontWeight:600,textAlign:"left",borderBottom:"1px solid var(--iio-border-subtle)",whiteSpace:"nowrap"}}>{c}</th>)}</tr></thead>
                  <tbody>{parsed.rows.map((row,i)=><tr key={i}>{parsed.cols.map(c=><td key={c} style={{padding:"2px 4px",color:"var(--iio-text-1)",borderBottom:"1px solid var(--iio-border-subtle)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row[c]||"\u2014"}</td>)}</tr>)}</tbody>
                </table>
              </div>:<span style={{color:"var(--iio-text-1)",fontFamily:"var(--iio-font-mono)",wordBreak:"break-all"}}>{val||t("bauteil.vergleich.leer")}</span>}
            </div>;
          })}
        </div>
      </div>}
      {(info.activityType==="Script"||info.subActivityType==="GroovyScript"||info.subActivityType==="JavaScript"||info.cname==="GroovyScript"||info.properties?.script||info.properties?.scriptFilename||info.properties?.scriptName)&&(()=>{
        const scriptRef=info.properties?.script||info.properties?.scriptFilename||info.properties?.scriptName||"";
        const scriptFileName=scriptRef.split("/").pop();
        let scriptData=scripts?.[scriptFileName];
        if(!scriptData&&scripts){const k=Object.keys(scripts).find(k2=>k2.toLowerCase()===scriptFileName.toLowerCase());if(k)scriptData=scripts[k];}
        if(!scriptData&&scripts&&Object.keys(scripts).length>0){const k=Object.keys(scripts).find(k2=>k2.endsWith(".groovy")||k2.endsWith(".gsh")||k2.endsWith(".js"));if(k)scriptData=scripts[k];}
        const content=side==="before"?(scriptData?.before||scriptData?.after):scriptData?.after;
        const resolvedName=scriptFileName||Object.keys(scripts||{})[0]||"Script";
        if(!content)return <div style={{marginTop:8}}>
          <div style={{fontSize:9,fontWeight:600,color:"var(--iio-text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("bauteil.vergleich.script")}</div>
          <div style={{fontSize:9,color:"var(--iio-text-3)",fontStyle:"italic",padding:"4px 0"}}>{t("bauteil.vergleich.scriptNichtVerfuegbar")}</div>
        </div>;
        return <div style={{marginTop:8}}>
          <div style={{fontSize:9,fontWeight:600,color:"var(--iio-text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("bauteil.vergleich.scriptMitName",{name:resolvedName})}</div>
          <pre style={{margin:0,padding:"6px 8px",background:"var(--iio-bg-0)",border:"1px solid var(--iio-border-subtle)",borderRadius:4,fontSize:9,fontFamily:"var(--iio-font-mono)",color:"var(--iio-syntax-kw)",maxHeight:180,overflow:"auto",whiteSpace:"pre-wrap",wordBreak:"break-all"}}>
            {content.length>800?content.substring(0,800)+"\n[...]":content}
          </pre>
        </div>;
      })()}
    </div>
  </div>;
}

// ── Inspector-Ina: Diff-Legende (NEU / WEG / GEÄND.) ─────────────────────
// Bildsprache identisch zur zentralen IflowDiagram-Komponente: farbiges
// Badge (+/−/Δ) plus Klartext-Präfix, damit Farbe nie allein trägt.
// Die Eintraege sind KLICKBAR: ein Klick hebt nur die Elemente dieser Rubrik
// hervor (alles andere wird gedimmt), ein zweiter Klick hebt den Filter auf.

export function DiffLegend({counts,focus,onFocus}){
  const {t}=useTranslation("gemeinsam");
  // Dieselben Schluessel wie die Marken in IflowDiagram, damit Legende und
  // Karten-Praefix nie auseinanderlaufen.
  const items=[
    ["add","var(--iio-dia-diff-add)","+",t("bauteil.diff.neu"),counts?.add||0],
    ["del","var(--iio-dia-diff-del)","−",t("bauteil.diff.weg"),counts?.del||0],
    ["chg","var(--iio-dia-diff-chg)","Δ",t("bauteil.diff.geaendert"),counts?.chg||0],
  ];
  return <div role="group" style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",
    fontSize:11,padding:"3px 6px",borderRadius:"var(--iio-r-md)",
    background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)"}}>
    {items.map(([key,col,badge,lbl,n])=>{
      const active=focus===key;
      return <button key={key} type="button" onClick={()=>onFocus&&onFocus(active?null:key)}
        title={t("bauteil.vergleich.fokusTitel")} aria-pressed={active} disabled={n===0}
        style={{display:"flex",alignItems:"center",gap:6,background:active?"var(--iio-bg-3)":"none",
          border:active?`1px solid ${col}`:"1px solid transparent",borderRadius:6,padding:"3px 8px",
          cursor:n===0?"default":"pointer",fontFamily:"inherit",fontSize:11,
          opacity:n===0?0.35:focus&&!active?0.45:1}}>
        <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
          width:16,height:16,color:col,
          fontSize:11,fontWeight:700,lineHeight:1,fontFamily:"Source Sans 3,sans-serif"}}>{badge}</span>
        <span style={{fontWeight:700,letterSpacing:".04em",color:col}}>{lbl}</span>
        <span style={{color:"var(--iio-text-3)",fontFamily:"var(--iio-font-mono)",fontSize:10}}>{n}</span>
      </button>;
    })}
  </div>;
}

// ── Panel-Petra: Diagramm-Pane (zentraler IflowDiagram-Renderer) ─────────
// Rendering kommt vollständig aus IflowDiagram; hier nur Kopfzeile, Klick-
// Inspector und das Durchreichen der Diff-Marks/onlyChanges an die Komponente.

function ComparePane({xml,label,side,marks,onlyChanges,focus,parsedBefore,parsedAfter,diffIds,scripts}){
  const[inspected,setInspected]=useState(null);
  return <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>
    <div style={{fontSize:11,fontWeight:600,color:"var(--iio-text-2)",padding:"6px 10px",
      background:"var(--iio-bg-2)",border:"1px solid var(--iio-border)",borderBottom:"none",
      borderRadius:"var(--iio-r-md) var(--iio-r-md) 0 0",display:"flex",alignItems:"center",gap:8}}>
      <span style={{width:8,height:8,borderRadius:2,flexShrink:0,background:side==="before"?"var(--iio-critical)":"var(--iio-success)"}}/>
      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>
    </div>
    <div style={{flex:1,minHeight:380,position:"relative",overflow:"hidden",
      border:"1px solid var(--iio-border)",borderRadius:"0 0 var(--iio-r-md) var(--iio-r-md)"}}
      onClick={()=>setInspected(null)}>
      <IflowDiagram bpmnXml={xml} height="100%" marks={marks} onlyChanges={onlyChanges} focus={focus}
        onElementClick={(el)=>setInspected(el)} selectedId={inspected?.id}/>
      {inspected&&<ElementInspectPopup element={inspected} side={side}
        parsedBefore={parsedBefore||{}} parsedAfter={parsedAfter||{}} diffIds={diffIds}
        onClose={()=>setInspected(null)} scripts={scripts}/>}
    </div>
  </div>;
}

// ── Dialog-Diana: Main Compare Dialog ────────────────────────────────────

export default function BpmnCompareDialog({open,onClose,sourceInstanceId,targetInstanceId,artifactId,targetArtifactId,artifactName,sourcePackageId,targetPackageId,sourceVersion,targetVersion}){
  const{t}=useTranslation("gemeinsam");
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState(null);
  const[data,setData]=useState(null);

  // t bewusst NICHT in den Abhaengigkeiten: ein Sprachwechsel darf den Vergleich
  // nicht neu laden. Der Ersatztext greift nur, wenn die Schnittstelle gar keine
  // Meldung liefert.
  useEffect(()=>{
    if(!open||!artifactId)return;
    setLoading(true);setError(null);setData(null);
    api("/api/transport/compare-bpmn",{method:"POST",
      body:JSON.stringify({sourceInstanceId,targetInstanceId,artifactId,targetArtifactId:targetArtifactId||artifactId,sourcePackageId,targetPackageId})
    }).then(d=>{setData(d);setLoading(false);}).catch(e=>{setError(e.message||t("bauteil.vergleich.ladefehler"));setLoading(false);});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open,artifactId,sourceInstanceId,targetInstanceId,targetArtifactId,sourcePackageId,targetPackageId]);

  const tgtBpmn=data?.target?.bpmn;
  const srcBpmn=data?.source?.bpmn;
  const parsedBefore=useMemo(()=>tgtBpmn?parseBpmnElements(tgtBpmn):{}, [tgtBpmn]);
  const parsedAfter=useMemo(()=>srcBpmn?parseBpmnElements(srcBpmn):{}, [srcBpmn]);
  const diffIds=useMemo(()=>tgtBpmn&&srcBpmn?diffBpmnElements(parsedBefore,parsedAfter):null, [parsedBefore,parsedAfter,tgtBpmn,srcBpmn]);
  const hasChanges=diffIds&&(diffIds.added.length+diffIds.removed.length+diffIds.changed.length>0);
  const totalChanges=diffIds?(diffIds.added.length+diffIds.removed.length+diffIds.changed.length):0;
  // Marks fuer die zentrale IflowDiagram-Komponente. Semantik "was aendert der Transport":
  //   Vorher-/Ziel-Pane (alte Version) -> entfernte (WEG) + geaenderte (GEAEND.) Elemente
  //   Nachher-/Quell-Pane (neue Version) -> neue (NEU) + geaenderte (GEAEND.) Elemente
  const marksBefore=useMemo(()=>{
    if(!diffIds)return undefined;
    const m={};
    for(const id of diffIds.removed)m[id]="del";
    for(const id of diffIds.changed)m[id]="chg";
    return m;
  },[diffIds]);
  const marksAfter=useMemo(()=>{
    if(!diffIds)return undefined;
    const m={};
    for(const id of diffIds.added)m[id]="add";
    for(const id of diffIds.changed)m[id]="chg";
    return m;
  },[diffIds]);
  const[onlyChanges,setOnlyChanges]=useState(false);
  // Legenden-Filter: hebt nur EINE Diff-Rubrik hervor (null = alle).
  const[focus,setFocus]=useState(null);
  const scripts=data?.scripts||{};
  const scriptNames=Object.keys(scripts);
  const changedScripts=scriptNames.filter(n=>scripts[n]?.before!==scripts[n]?.after).length;
  const [activeTab,setActiveTab]=useState("diagram");
  useEffect(()=>{ if(open){ setActiveTab("diagram"); setOnlyChanges(false); setFocus(null); } },[open,artifactId]);
  const sourceError=data?.source?.error;
  const targetError=data?.target?.error;

  // Instanzname und Version kommen aus den Daten und bleiben unuebersetzt.
  const beforeLabel=t("bauteil.vergleich.paneVorher",{system:data?.target?.instanceName||t("bauteil.vergleich.zielsystem"),version:targetVersion?" v"+targetVersion:""});
  const afterLabel=t("bauteil.vergleich.paneNachher",{system:data?.source?.instanceName||t("bauteil.vergleich.quellsystem"),version:sourceVersion?" v"+sourceVersion:""});

  const tabDef=[
    {k:"diagram",l:t("bauteil.vergleich.tabDiagramm"),badge:totalChanges>0?totalChanges:null},
    {k:"scripts",l:t("bauteil.vergleich.tabScripte"),badge:changedScripts>0?changedScripts:null},
  ];

  return <MuiDialog open={open} onClose={onClose} maxWidth={false} fullWidth
    PaperProps={{className:"iio",sx:{
      maxWidth:"95vw",width:"95vw",height:"88vh",
      background:"var(--iio-bg-0)",
      border:"1px solid var(--iio-border)",
      borderRadius:"var(--iio-r-xl)",overflow:"hidden"
    }}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 20px",
      borderBottom:"1px solid var(--iio-border)",
      background:"var(--iio-bg-2)"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--iio-signal)" strokeWidth="2" strokeLinecap="round">
          <path d="M12 3v18M3 12h18M8 8l-4 4 4 4M16 8l4 4-4 4"/>
        </svg>
        <div style={{minWidth:0}}>
          <div style={{fontSize:14,fontWeight:600,color:"var(--iio-text-1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t("bauteil.vergleich.titel",{name:artifactName||artifactId})}</div>
          <div style={{fontSize:11,color:"var(--iio-text-3)",display:"flex",alignItems:"center",gap:8}}>
            {sourceVersion&&targetVersion&&<span style={{display:"inline-flex",alignItems:"center",gap:6}}>
              <span>v{targetVersion}</span>
              <span className="iio-mon2-chevflow iio-mon2-chevflow--static iio-mon2-chevflow--sm" aria-hidden="true"><i/><i/></span>
              <span style={{color:"var(--iio-success)",fontWeight:600}}>v{sourceVersion}</span>
            </span>}
          </div>
        </div>
      </div>
      <MuiIconButton onClick={onClose} size="small" sx={{color:"var(--iio-text-3)"}}><CloseIcon fontSize="small"/></MuiIconButton>
    </div>

    {/* Tab-Leiste: Diagramm | Scripte (gemeinsames iio-mon2-subtab-Muster) */}
    <div className="iio-mon2-subtabs" style={{margin:0,padding:"0 20px",background:"var(--iio-bg-1)"}}>
      {tabDef.map(tab=>(
        <button key={tab.k} className={`iio-mon2-subtab${activeTab===tab.k?" is-active":""}`} onClick={()=>setActiveTab(tab.k)}>
          {tab.l}
          {tab.badge!=null&&<span className="cnt" style={{color:activeTab===tab.k?"var(--iio-signal)":"var(--iio-text-3)",fontFamily:"var(--iio-font-mono)"}}>{tab.badge}</span>}
        </button>
      ))}
    </div>

    <div style={{flex:1,overflow:"auto",padding:16,display:"flex",flexDirection:"column",gap:8}}>
      {loading&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12}}>
        <div style={{width:32,height:32,border:"3px solid var(--iio-border)",borderTopColor:"var(--iio-signal)",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
        <div style={{fontSize:12,color:"var(--iio-text-3)"}}>{t("bauteil.vergleich.laedt")}</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>}

      {error&&<div style={{padding:"12px 14px",color:"var(--iio-critical)",fontSize:12,background:"var(--iio-bg-1)",borderRadius:"var(--iio-r-md)",borderLeft:"3px solid var(--iio-critical)",border:"1px solid var(--iio-border)"}}>
        {t("bauteil.vergleich.fehler",{meldung:error})}
      </div>}

      {!loading&&!error&&data&&<>
        {(sourceError||targetError)&&<div style={{padding:"10px 14px",borderRadius:"var(--iio-r-md)",background:"var(--iio-bg-1)",borderLeft:"3px solid var(--iio-warning)",border:"1px solid var(--iio-border)",fontSize:11,color:"var(--iio-warning)",display:"flex",flexDirection:"column",gap:4}}>
          {sourceError&&<div>{t("bauteil.vergleich.quellsystemFehler",{meldung:sourceError})}</div>}
          {targetError&&<div>{t("bauteil.vergleich.zielsystemFehler",{meldung:targetError})}</div>}
        </div>}

        {activeTab==="scripts"
          ?<ScriptDiffView scripts={scripts}/>
          :<>
            {hasChanges&&<div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <DiffLegend
                counts={{add:diffIds?.added.length||0,del:diffIds?.removed.length||0,chg:diffIds?.changed.length||0}}
                focus={focus} onFocus={setFocus}/>
              {/* Bei aktivem Legenden-Filter ist "Nur Aenderungen" wirkungslos (focus ist
                  strikt enger) - die Checkbox wird deshalb gesperrt statt still geschluckt. */}
              <label title={focus?t("bauteil.vergleich.nurAenderungenGesperrt"):undefined}
                style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto",fontSize:12,color:"var(--iio-text-2)",cursor:focus?"default":"pointer",userSelect:"none",opacity:focus?0.45:1}}>
                <input type="checkbox" checked={onlyChanges} disabled={!!focus} onChange={e=>setOnlyChanges(e.target.checked)} style={{accentColor:"var(--iio-signal)",cursor:focus?"default":"pointer",width:14,height:14}}/>
                {t("bauteil.vergleich.nurAenderungen")}
              </label>
            </div>}
            {!hasChanges&&tgtBpmn&&srcBpmn&&<div style={{padding:"8px 12px",borderRadius:"var(--iio-r-md)",background:"var(--iio-bg-1)",borderLeft:"3px solid var(--iio-success)",border:"1px solid var(--iio-border)",fontSize:11,color:"var(--iio-success)"}}>
              {t("bauteil.vergleich.keineStrukturaenderung")}{" "}
              {changedScripts>0
                ?t("bauteil.vergleich.aberSkripte",{count:changedScripts,reiter:t("bauteil.vergleich.tabScripte")})
                :t("bauteil.vergleich.evtlParameter")}
            </div>}
            {(srcBpmn||tgtBpmn)?<div style={{display:"flex",gap:8,flex:1,minHeight:0}}>
              {tgtBpmn
                ?<ComparePane xml={tgtBpmn} label={beforeLabel} side="before" marks={marksBefore} onlyChanges={onlyChanges} focus={focus} parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds} scripts={scripts}/>
                :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:6,fontSize:11,color:"var(--iio-text-3)"}}>{t("bauteil.vergleich.keinVorherBpmn")}</div>}
              {srcBpmn
                ?<ComparePane xml={srcBpmn} label={afterLabel} side="after" marks={marksAfter} onlyChanges={onlyChanges} focus={focus} parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds} scripts={scripts}/>
                :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:6,fontSize:11,color:"var(--iio-text-3)"}}>{t("bauteil.vergleich.keinNachherBpmn")}</div>}
            </div>
            :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"var(--iio-text-3)"}}>
              {t("bauteil.vergleich.keinBpmn")}
            </div>}
          </>}
      </>}
    </div>
  </MuiDialog>;
}
